import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import classNames from 'clsx';

import { selectIsUserSubscribed } from 'src/common/selectors/me';
import ContainerItemsLoader from '../containerItems/ContainerItemsLoader';
import CommonDialog from '../shared/dialog/CommonDialog';
import PillButton from '../shared/button/PillButton';
import SkyButton from '../shared/button/SkyButton';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';

import {
  closeInterestSelectorDialog,
  fetchItemsAndOpenIntSelDialog,
  openUpsellAndTrackActivity,
} from '../../actions/dialog';
import { logInterestSelectorAction } from '../../actions/logging';
import { bulkAddInterestSelectorItems } from '../../actions/favoriteInterests';
import { urlStringToComponents } from '../../utils/urlHelpers';
import keyMirror from '../../utils/keyMirror';

import feature from '../../constants/analytics/categoryActionLabel/feature';
import subscribe from '../../constants/analytics/categoryActionLabel/subscribe';
import { DISMISS, FOLLOW } from '../../constants/localizations/shared';
import { INTEREST_SELECTOR_SUBMIT_BUTTON_LABEL_TRANSLATION_KEY } from '../../constants/dialogs/interestSelector';
import {
  Container,
  Header,
  ContentContainer,
  Content,
  Buttons,
} from './CustomizableDialogView';

import css from './interestSelectorDialog.module.scss';

// exported for testing
export const errorLabelValues = keyMirror(
  'missingGuideId',
  'invalidNumberOfQueryParamsOnSuccessDeepLink',
  'invalidQueryParamOnSuccessDeepLink',
  'errorBulkAddingInterests',
);

// exported for testing
export class InterestSelectorDialog extends Component {
  static propTypes = {
    guideItem: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    isDialogOpen: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    breakpoint: PropTypes.number.isRequired,
    submitButtonLocalizationKey: PropTypes.string.isRequired,
    isUserSubscribed: PropTypes.bool.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.handleClose = this.handleClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    this.addInterests = this.addInterests.bind(this);
  }

  state = {
    selectedItemIds: {},
    selectedCount: 0,
    areButtonsDisabled: false,
  };

  componentDidUpdate(prevProps) {
    const { isDialogOpen, actions, guideItem } = this.props;
    if (isDialogOpen && isDialogOpen !== prevProps.isDialogOpen) {
      if (!guideItem.guideId) {
        return this.handleError(errorLabelValues.missingGuideId);
      }

      this.resetStateForOpenDialog();
      actions.logInterestSelectorAction(feature.labels.show, guideItem.guideId);
    }
  }

  resetStateForOpenDialog() {
    this.setState({
      selectedItemIds: {},
      selectedCount: 0,
      areButtonsDisabled: false,
      ...this.parseUpsellLabelandThemeTypeFromGuideItem(),
    });
  }

  // The Category API response contains a deeplink in the metadata field (example: `tunein://upsell?upsellscreen.override.themetype=mlb`)
  // This is used by mobile clients to open the upsell and log the upsell show event. Since we don't need the
  // deeplink, we're just parsing out:
  //  1. the label suffix to append to the upsell log
  //  2. the theme type for the upsell dialog
  parseUpsellLabelandThemeTypeFromGuideItem() {
    const successDeeplink =
      this.props.guideItem?.metadata?.properties?.continue?.successDeeplink ||
      '';
    const { queryParams } = urlStringToComponents(successDeeplink);
    const queryParamKeys = Object.keys(queryParams);

    // the deeplink should only have 1 query param
    if (queryParamKeys.length !== 1) {
      this.handleError(
        errorLabelValues.invalidNumberOfQueryParamsOnSuccessDeepLink,
      );
      return;
    }

    const queryParamKey = queryParamKeys[0];
    const queryParamValue = queryParams[queryParamKey];

    if (!queryParamValue) {
      this.handleError(errorLabelValues.invalidQueryParamOnSuccessDeepLink);
      return;
    }

    return {
      upsellLabelSuffix: `${queryParamKey}:${queryParamValue}`.toLowerCase(),
      themeType: queryParamValue.toLowerCase(),
    };
  }

  handleClose(
    event,
    eventLabel = feature.labels.dismiss,
    errorLabelValue = '',
  ) {
    if (event) event.preventDefault();
    const {
      actions,
      guideItem: { guideId = '' },
    } = this.props;
    let labelValue = '';

    if (eventLabel !== feature.labels.dismiss) {
      labelValue =
        guideId && errorLabelValue
          ? `${guideId}.${errorLabelValue}`
          : guideId || errorLabelValue;
    }

    actions.closeInterestSelectorDialog();
    actions.logInterestSelectorAction(eventLabel, labelValue);
  }

  handleItemClick(guideId) {
    this.setState(({ selectedItemIds, selectedCount }) => {
      const addOrSub = selectedItemIds[guideId] ? -1 : 1;

      return {
        selectedItemIds: {
          ...selectedItemIds,
          [guideId]: !selectedItemIds[guideId],
        },
        selectedCount: selectedCount + addOrSub,
      };
    });
  }

  handleSubmit() {
    const {
      actions,
      guideItem: { guideId },
      isUserSubscribed,
    } = this.props;
    const { selectedItemIds, selectedCount } = this.state;
    const selectedItemsArr = keys(pickBy(selectedItemIds, Boolean));

    if (!selectedCount) {
      return this.handleClose();
    }

    this.setState({ areButtonsDisabled: true }, async () => {
      // Awaiting this dispatch allows our setState callback to short-circuit silently if the API call fails.
      // If the API call fails, a redux reducer handles opening the error dialog.
      await this.addInterests(selectedItemsArr);

      if (!isUserSubscribed) {
        // Awaiting this dispatch ensures we open the new dialog before closing the current dialog
        await this.openUpsellDialog();
      }

      actions.closeInterestSelectorDialog();
      actions.logInterestSelectorAction(
        feature.labels.save,
        `${guideId}.[${selectedItemsArr.toString()}]`,
      );
    });
  }

  async addInterests(selectedItemsArr) {
    const { actions } = this.props;

    try {
      return await actions.bulkAddInterestSelectorItems(selectedItemsArr);
    } catch (e) {
      this.handleError(errorLabelValues.errorBulkAddingInterests);
      throw e;
    }
  }

  async openUpsellDialog() {
    const {
      actions,
      guideItem: { guideId },
    } = this.props;
    const { upsellLabelSuffix } = this.state;

    const upsellLabel = upsellLabelSuffix
      ? `${subscribe.labels.interestSelectorUpsell}.${upsellLabelSuffix}`
      : subscribe.labels.interestSelectorUpsell;
    await actions.openUpsellAndTrackActivity(upsellLabel, guideId);
  }

  handleError(errorLabelValue) {
    this.handleClose(null, feature.labels.error, errorLabelValue);
  }

  render() {
    const {
      guideItem,
      isDialogOpen,
      breakpoint,
      isMobile,
      submitButtonLocalizationKey,
    } = this.props;
    const { areButtonsDisabled } = this.state;
    const { getLocalizedText } = this.context;
    const headerStyles = { backgroundImage: `url("${guideItem.imageUrl}")` };

    if (!isDialogOpen || !guideItem.guideId) {
      return null;
    }

    return (
      <CommonDialog
        dialogOpen={isDialogOpen}
        handleDialogClose={this.handleClose}
        contentClassName={classNames({ [css.dialogContent]: !isMobile })}
        bodyClassName={css.dialogBody}
        enableFullScreen={isMobile}
        data-testid="interestSelectorDialog"
        hasDarkTheme
        hideCornerClose
        disableOverlayClickToClose
      >
        <Container
          className={classNames(css.container, { fullScreen: isMobile })}
        >
          <Header
            className={css.header}
            overlayClassName={css.headerOverlay}
            style={headerStyles}
          >
            <div className={css.title}>{guideItem.title}</div>
          </Header>
          <ContentContainer className={css.contentContainer}>
            <Content className={css.content}>
              <ContainerItemsLoader
                selectedContentId={guideItem.guideId}
                items={guideItem.containerItems}
                isFetching={guideItem.isFetching}
                breakpoint={breakpoint}
                className={css.containerItems}
                onItemClick={this.handleItemClick}
                isDarkMode
                disableFirstContainerAd
              />
            </Content>
            <Buttons className={css.buttons}>
              <SkyButton
                id="interestSelectorSubmitButton"
                onClick={this.handleSubmit}
                label={getLocalizedText(submitButtonLocalizationKey)}
                className={classNames(css.button, css.followButton)}
                isDisabled={areButtonsDisabled}
              />
              <PillButton
                id="interestSelectorDismissButton"
                onClick={this.handleClose}
                label={getLocalizedText(DISMISS)}
                className={classNames(css.button, css.cancelButton)}
                isDisabled={areButtonsDisabled}
              />
            </Buttons>
          </ContentContainer>
        </Container>
      </CommonDialog>
    );
  }
}

// exported for testing
export function mapStateToProps(state) {
  const {
    dialog,
    categories,
    config: { autoupdate },
    app,
  } = state;
  const submitButtonLocalizationKey = get(
    autoupdate,
    INTEREST_SELECTOR_SUBMIT_BUTTON_LABEL_TRANSLATION_KEY,
    FOLLOW,
  );
  const categoryId = get(dialog, 'interestSelectorCategoryId', '');

  return {
    isDialogOpen: dialog.isInterestSelectorDialogOpen,
    guideItem: get(categories, categoryId, {}),
    breakpoint: app.breakpoint,
    isMobile: app.isMobile,
    submitButtonLocalizationKey,
    isUserSubscribed: selectIsUserSubscribed(state),
  };
}

// exported for testing
export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        closeInterestSelectorDialog,
        logInterestSelectorAction,
        bulkAddInterestSelectorItems,
        fetchItemsAndOpenIntSelDialog,
        openUpsellAndTrackActivity,
      },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InterestSelectorDialog);
